import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCButton from '_components/inputs/LFCButton';
import ProgressBar from '_components/ProgressBar';
import {getSourceCollectors} from '_logics/LFCSourceUtil';
import {getLFCData, setLFCDataProc} from '_logics/LFCUtil';
import MapPointEditor, {MapIconProps} from 'pages/common/work-images/_components/MapPointEditor';
import MovingList, {ItemRow} from 'pages/common/work-images/_components/MovingList';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Slider,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';

const ICON_COLOR = 'rgb(255, 193, 7)';
const IMAGE_WIDTH_FALLBACK = 900;
const IMAGE_HEIGHT_FALLBACK = 600;
const IMAGE_ZOOM_MIN = 0.5;
const IMAGE_ZOOM_MAX = 1.5;
const IMAGE_ZOOM_STEP = 0.1;

interface Props {
  open: boolean;
  onClose: (isRefresh: boolean) => void;
  floorId: number;
  floorName: string;
  imageNo: number;
  imagePath: string;
  aspectRatio: number;
  iconRatio: number;
}

const SQL_ID_FLOOR_DEVICES = 8011;

const FloorDeviceDialog = (props: Props) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const [devices, setDevices] = useState<ItemRow[]>([]);
  const [selectDevices, setSelectDevices] = useState<ItemRow[]>([]);
  const [icons, setIcons] = useState<MapIconProps[]>([]);

  const [imageZoom, setImageZoom] = useState(1);
  const [originalImageWidth, setOriginalImageWidth] = useState(IMAGE_WIDTH_FALLBACK);
  const [availableImageWidth, setAvailableImageWidth] = useState(IMAGE_WIDTH_FALLBACK);
  const [availableImageHeight, setAvailableImageHeight] = useState(IMAGE_HEIGHT_FALLBACK);
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const [iconRatio, setIconRatio] = useState(1);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  // 1倍は、画面で利用できる幅・高さと元画像幅の小さい方を基準にする。
  // 高さはアスペクト比から幅へ換算し、縦長画像が画面高を超えないようにする。
  const safeAspectRatio =
    Number.isFinite(props.aspectRatio) && props.aspectRatio > 0 ? props.aspectRatio : 1;
  const maxWidthByHeight = availableImageHeight / safeAspectRatio;
  const baseImageWidth = Math.min(originalImageWidth, availableImageWidth, maxWidthByHeight);
  // 倍率は1倍時の表示幅を基準に適用する。拡大後は元画像幅で再制限しない。
  const imgWidth = Math.max(1, Math.round(baseImageWidth * imageZoom));
  const imgHeight = imgWidth * safeAspectRatio;

  const onIconChanged = (newIcons: MapIconProps[]) => {
    const newSelectDevices = selectDevices.filter(r => newIcons.some(r2 => r2.id === r.text));
    setSelectDevices(newSelectDevices);
    setIcons(newIcons);
  };

  const onListToEdit = (newSelectedDevices: ItemRow[]) => {
    setSelectDevices(newSelectedDevices);
    const diffDevices = newSelectedDevices.filter(r => !icons.some(r2 => r2.id === r.text));
    setIcons([
      ...icons,
      ...diffDevices.map((r, i) => ({
        id: r.text,
        color: ICON_COLOR,
        position: {relativeX: 2, relativeY: 2 + i}
      }))
    ]);
  };

  const onDelete = (isOk: boolean) => {
    if (!isOk) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      setLFCDataProc({
        snack: enqueueSnackbar,
        name: '削除処理',
        invoke_name: 'DeleteFloorImage',
        parameters: {floors: [{floorId: props.floorId, imageNo: props.imageNo}]},
        cancelToken: cancelSource.current.token,
        t
      })
        .then(() => onClose(true))
        .catch(err => {
          console.error(err);
          enqueueSnackbar(t('削除に失敗しました'), {variant: 'error'});
        })
    ]).then(() => setStartProcess(false));
  };

  const onClear = (isOk: boolean) => {
    if (!isOk) return;
    setSelectDevices([]);
    setIcons([]);
  };

  const onSave = () => {
    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      setLFCDataProc({
        snack: enqueueSnackbar,
        name: '登録処理',
        invoke_name: 'CreateFloorDevices',
        parameters: {
          floorId: props.floorId,
          imageNo: props.imageNo,
          devices: icons.map(r => ({
            device: r.id,
            relative_x: r.position.relativeX,
            relative_y: r.position.relativeY
          })),
          icon_ratio: iconRatio
        },
        cancelToken: cancelSource.current.token,
        t
      })
        .then(() => onClose(true))
        .catch(err => {
          console.error(err);
          enqueueSnackbar(t('登録に失敗しました'), {variant: 'error'});
        })
    ]).then(() => setStartProcess(false));
  };

  const onClose = (isRefresh: boolean) => {
    props.onClose(isRefresh);
  };

  useEffect(() => {
    return () => {
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  useEffect(() => {
    if (!props.open || !containerElement) return;

    const updateWidth = () => {
      const maxW = containerElement.offsetWidth - 400;
      setAvailableImageWidth(Math.max(1, maxW * 0.9));
      setAvailableImageHeight(Math.max(1, containerElement.offsetHeight * 0.9));
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerElement);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [containerElement, props.open]);

  useEffect(() => {
    if (!props.open) {
      setDevices([]);
      setImageZoom(1);
      setOriginalImageWidth(IMAGE_WIDTH_FALLBACK);
      setAvailableImageWidth(IMAGE_WIDTH_FALLBACK);
      setAvailableImageHeight(IMAGE_HEIGHT_FALLBACK);
      setIconRatio(1);
      setSelectDevices([]);
      setIcons([]);
      return;
    }

    setDevices([]);
    setImageZoom(1);
    setOriginalImageWidth(IMAGE_WIDTH_FALLBACK);
    setAvailableImageHeight(IMAGE_HEIGHT_FALLBACK);
    setIconRatio(props.iconRatio);

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();

    getSourceCollectors()
      .then(collectors => {
        const tmp = collectors.map((name, i) => ({idx: i, text: name}));
        if (tmp.length === 0) setStartProcess(false);
        setDevices(tmp);
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar(t('端末一覧の取得に失敗しました'), {variant: 'error'});
        setStartProcess(false);
      });
  }, [props.open]);

  // 端末リスト取得後に保存済み座標を取得
  useEffect(() => {
    if (!props.open || devices.length === 0) return;

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: SQL_ID_FLOOR_DEVICES,
        parameters: {floor_id: props.floorId, image_no: props.imageNo},
        cancelToken: cancelSource.current.token,
        t
      }).then((ds: any[]) => {
        if (ds.length === 0) {
          setSelectDevices([]);
          setIcons([]);
          return;
        }

        const savedIcons: MapIconProps[] = ds.map(item => ({
          id: item['device'],
          color: ICON_COLOR,
          position: {relativeX: item['relative_x'], relativeY: item['relative_y']}
        }));

        const newSelectDevices = devices.filter(r => savedIcons.some(r2 => r2.id === r.text));
        setSelectDevices(newSelectDevices);
        setIcons(savedIcons);
      })
    ]).then(() => setStartProcess(false));
  }, [devices]);

  const handleImageLoad = (naturalWidth: number) => {
    if (!Number.isFinite(naturalWidth) || naturalWidth <= 0) return;
    setOriginalImageWidth(naturalWidth);
  };

  return (
    <Dialog open={props.open} onClose={() => onClose(false)} fullScreen>
      <ProgressBar startProcess={startProcess} screenLock />
      <LFCDialogTitle onClose={() => onClose(false)}>{t('端末配置')}</LFCDialogTitle>
      <DialogContent
        ref={setContainerElement}
        dividers
        sx={{overflowY: 'auto', overflowX: 'hidden'}}
      >
        <Stack direction="row" spacing={1} sx={{width: '100%', minWidth: 0}}>
          <Box sx={{flexShrink: 0}}>
            <MovingList
              title={t('端末')}
              items={devices}
              selectItems={selectDevices}
              onSelectedChanged={onListToEdit}
              width="250px"
              height="80vh"
            />
          </Box>
          <Box width="100%" sx={{minWidth: 0}}>
            <Box mb={2} display="flex" alignItems="center" gap={2} sx={{minWidth: 0}}>
              <Box maxWidth="300px" flexShrink={0}>
                <Chip size="small" label={t('フロア名')} />
                <Tooltip title={props.floorName} arrow>
                  <Typography mt={0.5} ml={1} noWrap>
                    {props.floorName}
                  </Typography>
                </Tooltip>
              </Box>
              <Box width="100px" flexShrink={0}>
                <Chip size="small" label={t('画像No')} />
                <Typography mt={0.5} ml={1}>
                  {props.imageNo}
                </Typography>
              </Box>
              <Box width="200px" flexShrink={0}>
                <Chip size="small" variant="outlined" label={t('アイコンサイズ')} />
                <Box ml={1}>
                  <Stack spacing={1} direction="row" sx={{alignItems: 'center'}}>
                    <RemoveIcon />
                    <Slider
                      size="small"
                      valueLabelDisplay="off"
                      value={iconRatio}
                      step={0.1}
                      min={0.5}
                      max={1.5}
                      onChange={(_, newValue) => setIconRatio(newValue as number)}
                    />
                    <AddIcon />
                  </Stack>
                </Box>
              </Box>
              <Box flexGrow={1} />
              <Box width="175px" flexShrink={0}>
                <Stack spacing={1} direction="row" sx={{alignItems: 'center', mb: 1}}>
                  <ZoomOutIcon />
                  <Slider
                    aria-label={t('画像倍率')}
                    size="small"
                    valueLabelDisplay="auto"
                    valueLabelFormat={value => `${value.toFixed(1)}x`}
                    value={imageZoom}
                    step={IMAGE_ZOOM_STEP}
                    min={IMAGE_ZOOM_MIN}
                    max={IMAGE_ZOOM_MAX}
                    onChange={(_, newValue) => setImageZoom(newValue as number)}
                  />
                  <ZoomInIcon />
                </Stack>
              </Box>
            </Box>
            <Box sx={{minWidth: 0}}>
              <Box sx={{width: '100%', maxWidth: '100%', overflow: 'auto'}}>
                <MapPointEditor
                  containerWidth={imgWidth}
                  containerHeight={imgHeight}
                  imgPath={props.imagePath}
                  icons={icons}
                  iconRatio={iconRatio}
                  iconSizeBasis="longest-side"
                  onIconChanged={onIconChanged}
                  onImageLoad={handleImageLoad}
                />
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="start" width="100%">
          <LFCButton
            color="secondary"
            onClick={onDelete}
            confirmMessage={t('設定された画像とアイコンを全てクリアします。よろしいですか？')}
          >
            {t('画像削除')}
          </LFCButton>
        </Box>
        <Box display="flex" justifyContent="end" width="100%">
          <LFCButton
            color="secondary"
            onClick={onClear}
            confirmMessage={t('設定されたアイコンを全てクリアします。よろしいですか？')}
          >
            {t('全クリア')}
          </LFCButton>
          <Box pl={5}>
            <LFCButton onClick={() => onClose(false)}>{t('キャンセル')}</LFCButton>
          </Box>
          <Box pl={1}>
            <LFCButton color="primary" onClick={onSave}>
              {t('登録')}
            </LFCButton>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FloorDeviceDialog;
